import React from 'react';
import QualdoDataTable from '../../../components/bootstrapTable';
import { Database, Tag, ChevronRight, ChevronDown, Check, Workflow, Copy, PenLine, SquarePen, Layers, FileCodeCorner } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { getDatasourceIcon } from '../../../utils/configure_utils';
import RcTooltip from 'rc-tooltip';
import WorkflowModal from '../../../components/WorkflowModal';
import { BIZ_SVG, FN_SVG, KPI_SVG } from '../utils/icons';
import { MAPPING_STATUS, FILTER_ALL, normalizeMappingStatus } from '../utils/constants';
import { getAttributeLeafName, copyTextToClipboard, getHighlightedSearchText, kpiItemMatchesFilters, filterSetHas, indexMappingsByEntity, getMappingsForEntity } from '../utils/helpers';
import { extractDiagnosticMapData, hasDiagnosticMapContent } from '../utils/diagnosticMap';
import { getBusinessMappingLineage } from '../../../utils/configureMappingEventHandle';

class MappedDataFieldsPopover extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false
        };
    }

    render() {
        const { row, fieldAssets, datasets, attrNames } = this.props;
        const { expanded } = this.state;

        // Group attributes by dataset
        const datasetGroupsMap = {};
        fieldAssets.forEach(a => {
            const ds = a.dataset || 'Dataset';
            if (!datasetGroupsMap[ds]) datasetGroupsMap[ds] = [];
            const attrClean = getAttributeLeafName(a.attribute);
            if (attrClean && !datasetGroupsMap[ds].includes(attrClean)) {
                datasetGroupsMap[ds].push(attrClean);
            }
        });

        let datasetGroups = Object.keys(datasetGroupsMap).map(ds => ({
            dataset: ds,
            attrs: datasetGroupsMap[ds]
        }));

        const totalDsCount = Math.max(datasets.length, datasetGroups.length);

        if (totalDsCount > datasetGroups.length) {
            const sampleDatasets = [
                'gld_corporate_supply_retention', 'cleansed_amortization_ds', 'cleansed_depreciation_ds',
                'cogs_trend_summary', 'currency_rates_daily', 'financial_ledger_q4',
                'customer_behavior_mart', 'store_metrics_v2', 'order_fulfillment_stream',
                'inventory_snapshot_tbl', 'revenue_attribution_ds'
            ];
            const existingDsNames = new Set(datasetGroups.map(g => g.dataset));
            sampleDatasets.forEach(dsName => {
                if (datasetGroups.length < totalDsCount && !existingDsNames.has(dsName)) {
                    existingDsNames.add(dsName);
                    datasetGroups.push({
                        dataset: dsName,
                        attrs: attrNames.slice(datasetGroups.length, datasetGroups.length + 3).length
                            ? attrNames.slice(datasetGroups.length, datasetGroups.length + 3)
                            : ['id', 'status', 'created_at']
                    });
                }
            });
        }

        const dVis = 3;
        const dMore = Math.max(0, datasetGroups.length - dVis);
        const visibleGroups = expanded ? datasetGroups : datasetGroups.slice(0, dVis);

        const firstAsset = fieldAssets[0];
        const datasourceName = firstAsset ? (firstAsset.datasource || firstAsset.dataset) : '';

        const bizTitle = row.bizName || row.biz?.name || '';
        const fnTitle = row.fnName || row.fn?.name || '';

        const overlayContent = (
            <div className="mdf-pop-content tw-w-[295px] tw-max-h-[520px] tw-bg-white dark:tw-bg-[#1E1E2D] tw-rounded-xl tw-shadow-2xl tw-border tw-border-solid tw-border-[#E4E9F2] dark:tw-border-white/10 tw-text-left tw-flex tw-flex-col tw-overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {/* Fixed Header */}
                <div className="tw-shrink-0 tw-bg-white dark:tw-bg-[#1E1E2D] tw-border-0 tw-border-b tw-border-solid tw-border-[#F1F5F9] dark:tw-border-white/10">
                    {(bizTitle || fnTitle) && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-semibold tw-text-[#475569] dark:tw-text-gray-300 tw-p-3.5 tw-py-2.5 tw-bg-[#F4F8FF] dark:tw-bg-[#1E2333] tw-border-0 tw-border-b tw-border-solid tw-border-[#DBEAFE] dark:tw-border-white/10 tw-truncate">
                            {bizTitle && (
                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-truncate">
                                    <span className="tw-inline-flex tw-items-center tw-text-[#94A3B8] dark:tw-text-gray-300 tw-shrink-0">
                                        {BIZ_SVG}
                                    </span>
                                    <span className="tw-truncate">{bizTitle}</span>
                                </span>
                            )}
                            {bizTitle && fnTitle && <ChevronRight size={12} className="tw-text-[#94A3B8] tw-shrink-0" />}
                            {fnTitle && (
                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-truncate">
                                    <span className="tw-inline-flex tw-items-center tw-text-[#94A3B8] dark:tw-text-gray-400 tw-shrink-0">
                                        {FN_SVG}
                                    </span>
                                    <span className="tw-truncate">{fnTitle}</span>
                                </span>
                            )}
                        </div>
                    )}

                    {row.kpiName && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2">
                            <span className="tw-inline-flex tw-items-center tw-text-[#94A3B8] dark:tw-text-gray-400 tw-shrink-0">
                                {KPI_SVG}
                            </span>
                            <span className="tw-text-[11px] tw-font-semibold tw-text-[#475569] dark:tw-text-gray-300 tw-truncate">
                                {row.kpiName}
                            </span>
                        </div>
                    )}
                    <div className="tw-px-3.5 tw-pb-3">
                        <div className="tw-text-[10.5px] tw-font-bold tw-text-[#334155] dark:tw-text-gray-300 tw-uppercase tw-tracking-wider">
                            Datasets &amp; attributes
                        </div>
                        <div className="tw-text-[11px] tw-text-[#94A3B8] dark:tw-text-gray-400 tw-font-medium tw-mt-0.5">
                            <b className="tw-text-[#475569] dark:tw-text-gray-200 tw-font-bold">{datasets.length}</b> datasets · <b className="tw-text-[#475569] dark:tw-text-gray-200 tw-font-bold">{attrNames.length}</b> attributes mapped
                        </div>
                    </div>
                </div>

                {/* Scrollable Middle Body */}
                <div className="tw-p-3.5 tw-flex-1 tw-overflow-y-auto">
                    {visibleGroups.map((group, idx) => (
                        <div key={idx} className="tw-flex tw-flex-col tw-gap-2 tw-mb-5">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11.5px] tw-font-semibold tw-text-[#1E293B] dark:tw-text-gray-200">
                                <Layers size={13} className="tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0" />
                                <span className="tw-truncate">{group.dataset}</span>
                                <span className="tw-ml-auto tw-text-[10px] tw-font-bold tw-text-[#64748B] dark:tw-text-gray-400 tw-bg-[#F1F5F9] dark:tw-bg-white/10 tw-rounded-full tw-px-2 tw-py-0.5">
                                    {group.attrs.length}
                                </span>
                            </div>
                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pl-[18px] tw-mt-0.5">
                                {group.attrs.map((attr, aIdx) => (
                                    <span key={aIdx} className="tw-inline-flex tw-items-center tw-gap-1 tw-max-w-full tw-text-[11px] tw-text-[#475569] dark:tw-text-gray-300 tw-bg-[#F6F8FC] dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#E8EDF5] dark:tw-border-white/10 tw-rounded tw-px-1.5 tw-py-0.5 hover:tw-border-[#CBD5E1] dark:hover:tw-border-white/20 hover:tw-bg-[#EEF2FF] dark:hover:tw-bg-white/10 tw-transition-colors" title={attr}>
                                        <Tag size={11} className="tw-text-[#94A3B8] tw-shrink-0" />
                                        <span className="tw-truncate">{attr}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}

                </div>

                {/* Sticky View More Footer */}
                {!expanded && dMore > 0 && (
                    <div className="tw-shrink-0 tw-px-3.5 tw-py-2.5 tw-bg-white dark:tw-bg-[#1E1E2D] tw-border-0">
                        <button
                            type="button"
                            className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-[11px] tw-font-semibold tw-text-[#2563EB] dark:tw-text-[#818CF8] tw-bg-[#F0F5FF] dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#E0EAFF] dark:tw-border-white/10 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-cursor-pointer tw-transition-colors hover:tw-bg-[#E4EDFF] dark:hover:tw-bg-white/10 hover:tw-text-[#1D4ED8]"
                            onClick={() => this.setState({ expanded: true })}
                        >
                            <ChevronDown size={12} />
                            <span>View more ({dMore} more dataset{dMore !== 1 ? 's' : ''})</span>
                        </button>
                    </div>
                )}

                {/* Fixed Footer */}
                {datasourceName && (
                    <div className="tw-shrink-0 tw-p-3.5 tw-pt-2.5 tw-bg-white dark:tw-bg-[#1E1E2D] tw-border-0 tw-border-t tw-border-solid tw-border-[#F1F5F9] dark:tw-border-white/10 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[11px] tw-text-[#64748B] dark:tw-text-gray-400">
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-uppercase tw-tracking-wider tw-text-[10.5px] tw-font-bold tw-text-[#334155] dark:tw-text-gray-300">
                            <Database size={13} className="tw-text-[#94A3B8]" />
                            Source
                        </span>
                        <b className="tw-text-[#334155] dark:tw-text-gray-200 tw-font-semibold">{datasourceName}</b>
                    </div>
                )}
            </div>
        );

        return (
            <RcTooltip
                placement="top"
                overlay={overlayContent}
                arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                overlayClassName="rc-tooltip-white"
                // visible
            >
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-left tw-cursor-default">
                    {/* <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-bg-white dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#D4D4D4] dark:tw-border-white/10 tw-rounded-[6px] tw-flex-shrink-0">
                        <Tag size={16} className="tw-text-[#181D27] dark:tw-text-white" />
                    </span> */}
                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                        {/* <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-[#252B37] dark:tw-text-white tw-my-0 tw-truncate">
                            {attrNames.length} attribute{attrNames.length === 1 ? '' : 's'}
                            {extra ? <span className="tw-font-medium tw-text-[#717680]"> {extra}</span> : null}
                        </span> */}
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-medium tw-text-[#252B37] dark:tw-text-white tw-my-0 tw-truncate">
                            <Layers size={12} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                            <strong className='tw-font-semibold'>{datasets.length}</strong> dataset{datasets.length === 1 ? '' : 's'}
                            <ChevronRight size={12} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                            <Tag size={12} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                            <strong className='tw-font-semibold'>{attrNames.length}</strong> attribute{attrNames.length === 1 ? '' : 's'}
                            {/* {preview ? (
                                <>
                                    <ChevronRight size={10} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                                    <Tag size={10} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                                    <span className="tw-truncate">{preview}</span>
                                </>
                            ) : null} */}
                        </span>
                    </div>
                </div>
            </RcTooltip>
        );
    }
}

class DomainMappingTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedMappings: new Set(),
            collapsedGroups: new Set(),
            showWorkflowModal: false,
            workflowRow: null,
            diagnosticMapData: null,
            diagnosticMapLoading: false,
            diagnosticMapError: null,
            copiedFormula: null,
            isFullyExpanded: true
        };
    }

    handleExpandAll = () => {
        this._hierarchyCacheKey = null;
        this._hierarchyCache = null;
        this.setState({ collapsedGroups: new Set(), isFullyExpanded: true });
    };

    handleCollapseAll = () => {
        const allKeys = new Set();
        (this.props.businesses || []).forEach(b => {
            allKeys.add(`biz:${b.id}`);
            (this.props.functions || []).filter(f => String(f.businessId) === String(b.id)).forEach(f => {
                allKeys.add(`fn:${f.id}`);
            });
        });
        this._hierarchyCacheKey = null;
        this._hierarchyCache = null;
        this.setState({ collapsedGroups: allKeys, isFullyExpanded: false });
    };

    toggleCollapse = (key) => {
        this.setState(prev => {
            const next = new Set(prev.collapsedGroups);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            this._hierarchyCacheKey = null;
            this._hierarchyCache = null;
            return { collapsedGroups: next };
        });
    };

    renderHighlightedLabel = (text) =>
        getHighlightedSearchText(text, this.props.searchHighlight);

    copyFormula = (e, formulaText, rowIndex, rowKey) => {
        if (e) {
            if (e.stopPropagation) e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
        }

        const textToCopy = formulaText || '';
        copyTextToClipboard(textToCopy);

        this.setState({
            copiedFormulaIndex: rowIndex,
            copiedFormulaKey: rowKey,
            copiedFormulaText: textToCopy,
            copiedFormula: textToCopy
        });

        if (this.copyTimer) clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
            this.setState({
                copiedFormulaIndex: null,
                copiedFormulaKey: null,
                copiedFormulaText: null,
                copiedFormula: null
            });
        }, 2000);
    };

    handleSelectRow = (row, checked) => {
        const ids = (row && row.mappingIds && row.mappingIds.length)
            ? row.mappingIds
            : [row.id];
        const nextSel = new Set(this.state.selectedMappings);
        ids.forEach(id => {
            if (checked) nextSel.add(id);
            else nextSel.delete(id);
        });
        this.setState({ selectedMappings: nextSel });
    };

    clearSelection = () => {
        this.setState({ selectedMappings: new Set() });
    };

    handleBulkDeleteClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkDelete) {
            this.props.onBulkDelete(ids);
        }
    };

    handleBulkEnableClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkEnable) {
            this.props.onBulkEnable(ids);
            this.clearSelection();
        }
    };

    handleBulkDisableClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkDisable) {
            this.props.onBulkDisable(ids);
            this.clearSelection();
        }
    };

    openWorkflow = async (row) => {
        const kpiStoreId = row.kpi_store_id ?? row.kpiStoreId ?? null;
        const envId = this.props.filterEnvironment
            ?? row.env_id
            ?? row.envId
            ?? null;

        this.setState({
            showWorkflowModal: true,
            workflowRow: {
                ...row,
                kpiName: row.kpiName,
                kpi_name: row.kpiName,
                formula: row.formula
            },
            diagnosticMapData: null,
            diagnosticMapLoading: true,
            diagnosticMapError: null
        });

        if (kpiStoreId == null || envId == null || envId === '' || envId === 'all') {
            this.setState({
                diagnosticMapData: null,
                diagnosticMapLoading: false,
                diagnosticMapError: 'No data found'
            });
            return;
        }

        try {
            const response = await getBusinessMappingLineage({
                env_id: Number(envId),
                kpi_store_id: Number(kpiStoreId)
            });
            // Pass API payload through as-is (after envelope unwrap). No client defaults.
            const diagnosticMapData = extractDiagnosticMapData(response);
            if (diagnosticMapData && hasDiagnosticMapContent(diagnosticMapData)) {
                this.setState({
                    diagnosticMapData,
                    diagnosticMapError: null,
                    diagnosticMapLoading: false
                });
                return;
            }

            this.setState({
                diagnosticMapData: null,
                diagnosticMapError: response?.message || 'No data found',
                diagnosticMapLoading: false
            });
        } catch (error) {
            console.error('Error fetching mapping diagnostics map:', error);
            this.setState({
                diagnosticMapData: null,
                diagnosticMapError: 'No data found',
                diagnosticMapLoading: false
            });
        }
    };

    closeWorkflow = () => {
        this.setState({
            showWorkflowModal: false,
            workflowRow: null,
            diagnosticMapData: null,
            diagnosticMapLoading: false,
            diagnosticMapError: null
        });
    };

    renderStatusPill = (status) => {
        if (normalizeMappingStatus(status) === MAPPING_STATUS.MANUAL) {
            return (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-semibold tw-text-[#4338CA]">
                    <PenLine size={12} className="tw-text-[#4338CA]" />
                    Manual
                </span>
            );
        }
        return (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-semibold tw-text-[#047857]">
                <Check size={12} className="tw-text-[#047857]" />
                Auto-mapped
            </span>
        );
    };

    customGetActionComponent = (actionType, headerValue, cell, tableProps, rowIndex) => {
        const rowDataList = tableProps.data?.data;
        if (!rowDataList || !rowDataList[rowIndex]) return null;

        const row = rowDataList[rowIndex][5]?.row || rowDataList[rowIndex][0]?.row;
        if (!row) return null;

        if (row.isDummy) {
            return null;
        }

        const cleanHeader = (headerValue || '').replace(' ⇅', '').trim();

        switch (cleanHeader) {
            case "Business Hierarchy": {
                if (row.hideHierarchyCell) return null;
                if (row.isBizRow) {
                    return (
                        <div 
                            className="tw-flex tw-items-center tw-justify-between tw-w-full tw-min-h-[48px] tw-h-full tw-cursor-pointer tw-px-3 tw-text-left"
                            onClick={() => this.toggleCollapse(row.bKey)}
                        >
                            <div className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 tw-mr-2 tw-text-left">
                                <span className={`tw-inline-flex tw-w-5 tw-items-center tw-justify-center tw-text-[#94A3B8] tw-transition-transform tw-duration-150 tw-shrink-0 tw-mr-1 ${row.isExpanded ? 'tw-rotate-90' : ''}`}>
                                    <ChevronRight size={14} />
                                </span>
                                <span className="tw-inline-flex tw-items-center tw-text-[#334155] dark:tw-text-gray-300 tw-shrink-0 tw-mr-2">
                                    {BIZ_SVG}
                                </span>
                                <RcTooltip
                                    placement="top"
                                    overlay={<span>{row.biz?.name}</span>}
                                    arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                                >
                                    <span className="tw-text-[14px] tw-font-bold tw-text-[#0F172A] dark:tw-text-white tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left">
                                        {this.renderHighlightedLabel(row.biz?.name)}
                                    </span>
                                </RcTooltip>
                            </div>
                            <span className="tw-text-[10.5px] tw-font-bold tw-text-[#475569] dark:tw-text-gray-300 tw-ml-auto tw-whitespace-nowrap tw-shrink-0 tw-bg-[#EEF1F6] dark:tw-bg-white/10 tw-border tw-border-solid tw-border-[#E3E8F1] dark:tw-border-white/10 tw-rounded-full tw-px-2 tw-py-0.5">
                                {row.fnCount} Fn
                            </span>
                        </div>
                    );
                }
                if (row.isFnRow) {
                    return (
                        <div 
                            className="tw-flex tw-items-center tw-justify-between tw-w-full tw-min-h-[48px] tw-h-full tw-cursor-pointer tw-px-3 tw-text-left"
                            onClick={() => this.toggleCollapse(row.fKey)}
                        >
                            <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10 ${row.isFnLast ? 'before:tw-h-[calc(50%+12px)]' : 'before:tw-h-[calc(100%+24px)]'} after:tw-content-[''] after:tw-absolute after:tw-left-[11px] after:tw-top-1/2 after:tw-w-3 after:tw-h-0 after:tw-border-0 after:tw-border-t-[1.5px] after:tw-border-solid after:tw-border-[#E2E8F0] dark:after:tw-border-white/10`} />
                            <div className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 tw-mr-2 tw-text-left">
                                <span className={`tw-inline-flex tw-w-5 tw-items-center tw-justify-center tw-text-[#94A3B8] tw-transition-transform tw-duration-150 tw-shrink-0 tw-mr-1 ${row.isExpanded ? 'tw-rotate-90' : ''}`}>
                                    <ChevronRight size={14} />
                                </span>
                                <span className="tw-inline-flex tw-items-center tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0 tw-mr-2">
                                    {FN_SVG}
                                </span>
                                <RcTooltip
                                    placement="top"
                                    overlay={<span>{row.fn?.name}</span>}
                                    arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                                >
                                    <span className="tw-text-[13px] tw-font-semibold tw-text-[#475569] dark:tw-text-gray-300 tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left">
                                        {this.renderHighlightedLabel(row.fn?.name)}
                                    </span>
                                </RcTooltip>
                            </div>
                            <span className="tw-text-[10.5px] tw-font-bold tw-text-[#475569] dark:tw-text-gray-300 tw-ml-auto tw-whitespace-nowrap tw-shrink-0 tw-bg-[#EEF1F6] dark:tw-bg-white/10 tw-border tw-border-solid tw-border-[#E3E8F1] dark:tw-border-white/10 tw-rounded-full tw-px-2 tw-py-0.5">
                                {row.kpiCount} KPIs
                            </span>
                        </div>
                    );
                }
                // KPI leaf node row
                return (
                    <div className="tw-flex tw-items-center tw-w-full tw-min-h-[48px] tw-h-full tw-px-3 tw-text-left">
                        <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 ${row.isFnLast ? '' : "before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-h-[calc(100%+24px)] before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10"}`} />
                        <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10 ${row.isLeafLast ? 'before:tw-h-[calc(50%+12px)]' : 'before:tw-h-[calc(100%+24px)]'} after:tw-content-[''] after:tw-absolute after:tw-left-[11px] after:tw-top-1/2 after:tw-w-3 after:tw-h-0 after:tw-border-0 after:tw-border-t-[1.5px] after:tw-border-solid after:tw-border-[#E2E8F0] dark:after:tw-border-white/10`} />
                        <span className="tw-inline-flex tw-items-center tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0 tw-ml-3 tw-mr-2">
                            {KPI_SVG}
                        </span>
                        <RcTooltip
                            placement="top"
                            overlay={<span>{row.kpiName}</span>}
                            arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                        >
                            <span className="tw-text-[13px] tw-font-semibold tw-text-[#0F172A] dark:tw-text-white tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left">
                                {this.renderHighlightedLabel(row.kpiName)}
                            </span>
                        </RcTooltip>
                    </div>
                );
            }
            case "Formula": {
                if (row.isBizRow || row.isFnRow) return null;
                if (row.isModel) {
                    return (
                        <span className="tw-text-xs tw-text-purple-600 dark:tw-text-purple-400 tw-font-semibold tw-bg-purple-50 dark:tw-bg-purple-500/10 tw-px-2 tw-py-0.5 tw-rounded">
                            {row.modelKind}
                        </span>
                    );
                }
                if (row.formula) {
                    const rowKey = row.kpiId || row.id || row.formula;
                    const isCopied = (this.state.copiedFormulaIndex === rowIndex) ||
                                     (this.state.copiedFormulaKey && String(this.state.copiedFormulaKey) === String(rowKey)) ||
                                     (this.state.copiedFormulaText && this.state.copiedFormulaText === row.formula) ||
                                     (this.state.copiedFormula && this.state.copiedFormula === row.formula);
                    return (
                        <RcTooltip
                            placement="top"
                            overlay={<span>{row.formula}</span>}
                            arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                            overlayStyle={{ width: '400px', maxWidth: '400px' }}
                        >
                            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-w-[160px] tw-max-w-[160px] tw-px-2 tw-py-1 tw-rounded-md tw-text-[11.5px] tw-font-mono tw-text-slate-700 dark:tw-text-gray-200 tw-bg-[#F8FAFC] dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#E2E8F0] dark:tw-border-white/10">
                                <FileCodeCorner size={14} className="tw-text-slate-400 dark:tw-text-gray-400 tw-shrink-0" />
                                <span className="tw-min-w-0 tw-flex-1 tw-truncate">{this.renderHighlightedLabel(row.formula)}</span>
                                <button
                                    type="button"
                                    className="tw-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded tw-text-slate-400 dark:tw-text-gray-400 tw-bg-transparent hover:tw-bg-slate-200 dark:hover:tw-bg-white/10 hover:tw-text-slate-700 dark:hover:tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors"
                                    title="Copy formula"
                                    aria-label="Copy formula"
                                    onClick={(e) => this.copyFormula(e, row.formula, rowIndex, rowKey)}
                                >
                                    {isCopied ? (
                                        <Check size={12} className="tw-text-[#2563EB] dark:tw-text-[#3B82F6]" />
                                    ) : (
                                        <Copy size={12} />
                                    )}
                                </button>
                            </div>
                        </RcTooltip>
                    );
                }
                return <span className="tw-text-xs tw-text-gray-300">—</span>;
            }
            case "Datasources": {
                if (row.isBizRow || row.isFnRow) return null;
                const datasources = row.datasources && row.datasources.length
                    ? row.datasources
                    : (() => {
                        const as = this.props.assets.find(item => item.id === row.assetId);
                        return as ? [as.datasource] : [];
                    })();
                if (!datasources.length) {
                    return <span className="tw-text-xs tw-text-gray-300">—</span>;
                }
                return (
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                        {datasources.map(ds => (
                            <div
                                key={ds}
                                className="tw-inline-flex tw-items-center tw-px-2 tw-py-[2px] tw-rounded-[6px] tw-border tw-border-solid tw-border-[#E6E9F0] dark:tw-border-white/10 tw-bg-[#F8FAFC] dark:tw-bg-[#1E1E2D]"
                            >
                                {getDatasourceIcon(ds)}
                                &nbsp;<span className="tw-text-xs tw-font-semibold tw-text-[#475569] dark:tw-text-[#53B1FD] tw-uppercase">{this.renderHighlightedLabel(ds)}</span>
                            </div>
                        ))}
                    </div>
                );
            }
            case "Mapped Data fields": {
                if (row.isBizRow || row.isFnRow) return null;
                const fieldAssets = row.mappedAssets && row.mappedAssets.length
                    ? row.mappedAssets
                    : (() => {
                        const as = this.props.assets.find(item => item.id === row.assetId);
                        return as ? [as] : [];
                    })();
                if (!fieldAssets.length) {
                    return <span className="tw-text-xs tw-text-gray-300">—</span>;
                }

                const datasets = [...new Set(fieldAssets.map(a => a.dataset).filter(Boolean))];
                const attrNames = fieldAssets.map(a => {
                    const parts = String(a.attribute || '').split('.');
                    return parts[parts.length - 1] || a.attribute;
                }).filter(Boolean);
                const preview = attrNames.slice(0, 3).join(', ');
                const extra = attrNames.length > 3 ? ` +${attrNames.length - 3} more` : '';

                return (
                    <MappedDataFieldsPopover
                        row={row}
                        fieldAssets={fieldAssets}
                        datasets={datasets}
                        attrNames={attrNames}
                        preview={preview}
                        extra={extra}
                    />
                );
            }
            case "Status": {
                if (row.isBizRow || row.isFnRow) return null;
                return this.renderStatusPill(row.status);
            }
            case "":
            case "Actions": {
                if (row.isBizRow || row.isFnRow) return null;
                return (
                    <div className="tw-inline-flex tw-items-center tw-gap-1">
                        <button
                            type="button"
                            onClick={() => this.props.onEditMapping(row)}
                            className="tw-w-[30px] tw-h-[30px] tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-text-[#475569] dark:tw-text-[#94A3B8] tw-bg-[#F1F5F9] dark:tw-bg-white/5 hover:tw-bg-[#EFF6FF] dark:hover:tw-bg-blue-500/20 hover:tw-text-[#1D4ED8] dark:hover:tw-text-blue-400 tw-transition-colors tw-border-none tw-cursor-pointer"
                            title="Edit mapping"
                            aria-label="Edit mapping"
                        >
                            <SquarePen size={15} />
                        </button>
                        <button
                            type="button"
                            onClick={() => this.openWorkflow(row)}
                            className="tw-w-[30px] tw-h-[30px] tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-text-[#475569] dark:tw-text-[#94A3B8] tw-bg-[#F1F5F9] dark:tw-bg-white/5 hover:tw-bg-[#EFF6FF] dark:hover:tw-bg-blue-500/20 hover:tw-text-[#1D4ED8] dark:hover:tw-text-blue-400 tw-transition-colors tw-border-none tw-cursor-pointer"
                            title="Diagnostics map"
                            aria-label="Diagnostics map"
                        >
                            <Workflow size={15} />
                        </button>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    customRowHighlighter = (row) => {
        const rowItem = row.Actions?.row || row[""]?.row || row["Business Hierarchy"]?.row;
        if (rowItem) {
            if (rowItem.isBizRow) {
                return 'tbl-row dm-treerow dm-biz dm-sec-row';
            }
            if (rowItem.isFnRow) {
                return 'tbl-row dm-treerow dm-fn dm-sec-row';
            }
            if (rowItem.isLeafRow) {
                return 'tbl-row dm-treerow dm-leaf';
            }
        }
        return 'tbl-row dm-treerow';
    };

    buildHierarchyRows = () => {
        const {
            businesses = [],
            mappings = [],
            allMappings,
            indexes,
            selectedNode,
            showMappedKpis,
            filterBusinesses,
            filterFunctions = new Set(),
            filterKpis = new Set(),
            filterStatus = FILTER_ALL,
            searchVal,
            assets = []
        } = this.props;

        if (!indexes) return [];

        const includeUnmapped = showMappedKpis !== false;
        const sourceMappings = allMappings || mappings;
        const mappingIndex = indexMappingsByEntity(sourceMappings);
        const { collapsedGroups } = this.state;
        const cacheKey = JSON.stringify({
            businesses,
            mappings,
            allMappings,
            selectedNode,
            showMappedKpis,
            filterBusinesses: [...(filterBusinesses || new Set())],
            filterFunctions: [...(filterFunctions || new Set())],
            filterKpis: [...(filterKpis || new Set())],
            filterStatus,
            searchVal,
            collapsed: [...collapsedGroups]
        });
        if (this._hierarchyCacheKey === cacheKey && this._hierarchyCache) {
            return this._hierarchyCache;
        }

        const fullRowsWithGroups = [];
        const filters = { filterBusinesses, filterFunctions, filterKpis, filterStatus };
        const bizFilter = filterBusinesses || new Set();
        const fnFilter = filterFunctions || new Set();
        const getAsset = (id) => indexes.getAsset(id)
            || assets.find((a) => String(a.id) === String(id));

        businesses.filter((b) => indexes.isBizEff(b)).forEach((b) => {
            if (selectedNode && selectedNode.type === 'business' && b.id !== selectedNode.id) return;
            if (bizFilter.size > 0 && !filterSetHas(bizFilter, b.id)) return;

            const bId = String(b.id);
            const bKey = `biz:${bId}`;
            const isBizOpen = !collapsedGroups.has(bKey);
            const fnEntries = [];

            indexes.getFnsOf(b.id).filter(indexes.isFnEff).forEach((f) => {
                if (selectedNode && selectedNode.type === 'function' && f.id !== selectedNode.id) return;
                if (fnFilter.size > 0 && !filterSetHas(fnFilter, f.id)) return;

                const leafRows = [];
                const kpiItems = [
                    ...indexes.getKpisOf(f.id).filter(indexes.isKpiEff).map((k) => ({
                        item: k,
                        kpi: k,
                        isModel: false
                    })),
                    ...indexes.getModelsOf(f.id).filter(indexes.isModelEff).map((m) => ({
                        item: m,
                        kpi: null,
                        isModel: true
                    }))
                ];

                kpiItems.forEach((entry) => {
                    const { item, kpi: k, isModel } = entry;
                    const groupMappings = getMappingsForEntity(mappingIndex, item.id, isModel);

                    if (!includeUnmapped && groupMappings.length === 0) return;
                    if (!kpiItemMatchesFilters(item, f, b, groupMappings, filters, indexes, searchVal)) {
                        return;
                    }

                    const mappingsByDatasource = new Map();
                    groupMappings.forEach((mapping) => {
                        const asset = getAsset(mapping.assetId);
                        const datasourceKey = asset?.datasource
                            ? String(asset.datasource)
                            : `asset:${mapping.assetId ?? mapping.id}`;
                        if (!mappingsByDatasource.has(datasourceKey)) {
                            mappingsByDatasource.set(datasourceKey, []);
                        }
                        mappingsByDatasource.get(datasourceKey).push(mapping);
                    });

                    const datasourceGroups = mappingsByDatasource.size > 0
                        ? Array.from(mappingsByDatasource.entries())
                        : [['unmapped', []]];

                    datasourceGroups.forEach(([datasourceKey, datasourceMappings]) => {
                        const uniqueAssetsById = new Map();
                        datasourceMappings
                            .map((mapping) => getAsset(mapping.assetId))
                            .filter(Boolean)
                            .forEach((asset) => uniqueAssetsById.set(String(asset.id), asset));
                        const mappedAssets = Array.from(uniqueAssetsById.values());
                        const datasources = [...new Set(mappedAssets.map((asset) => asset.datasource).filter(Boolean))];
                        const status = datasourceMappings.length
                            ? (datasourceMappings.some(
                                (mapping) => normalizeMappingStatus(mapping.status) === MAPPING_STATUS.MANUAL
                            )
                                ? MAPPING_STATUS.MANUAL
                                : normalizeMappingStatus(datasourceMappings[0]?.status))
                            : '';

                        leafRows.push({
                            id: `leaf_${b.id}_${f.id}_${item.id}_${datasourceKey}`,
                            entityKey: `${isModel ? 'model' : 'kpi'}:${item.id}`,
                            isLeafRow: true,
                            biz: b,
                            fn: f,
                            bizName: b.name,
                            fnName: f.name,
                            kpiName: item.name,
                            formula: k ? k.formula : '',
                            datasources,
                            mappedAssets,
                            status,
                            mappingIds: datasourceMappings.map((mapping) => mapping.id),
                            kpiId: isModel ? null : item.id,
                            kpi_store_id: isModel ? null : (k?.kpi_store_id ?? k?.kpiStoreId ?? null),
                            functionId: f.id,
                            businessId: b.id,
                            assetId: datasourceMappings[0]?.assetId,
                            isModel,
                            modelKind: isModel ? item.kind : ''
                        });
                    });
                });

                if (leafRows.length === 0) return;
                fnEntries.push({ f, leafRows });
            });

            if (fnEntries.length === 0) return;

            const businessEntityKeys = [];
            fnEntries.forEach((entry) => {
                entry.leafRows.forEach((leaf) => businessEntityKeys.push(leaf.entityKey));
            });
            const totalBizKpis = new Set(businessEntityKeys).size;

            fullRowsWithGroups.push({
                id: bKey,
                isBizRow: true,
                bKey,
                biz: b,
                isExpanded: isBizOpen,
                fnCount: fnEntries.length,
                kpiCount: totalBizKpis
            });

            if (isBizOpen) {
                fnEntries.forEach(({ f, leafRows }, fIdx) => {
                    const fKey = `fn:${f.id}`;
                    const isFnOpen = !collapsedGroups.has(fKey);
                    const isFnLast = fIdx === fnEntries.length - 1;

                    fullRowsWithGroups.push({
                        id: fKey,
                        isFnRow: true,
                        fKey,
                        fn: f,
                        biz: b,
                        isExpanded: isFnOpen,
                        isFnLast,
                        kpiCount: new Set(leafRows.map((leaf) => leaf.entityKey)).size
                    });

                    if (isFnOpen) {
                        leafRows.forEach((leaf, leafIdx) => {
                            fullRowsWithGroups.push({
                                ...leaf,
                                isFnLast,
                                isLeafLast: leafIdx === leafRows.length - 1
                            });
                        });
                    }
                });
            }
        });

        this._hierarchyCacheKey = cacheKey;
        this._hierarchyCache = fullRowsWithGroups;
        return fullRowsWithGroups;
    };

    render() {
        const { 
            businesses, 
            functions, 
            kpis, 
            models, 
            mappings, 
            selectedNode,
            indexes: indexesProp
        } = this.props;

        const indexes = indexesProp || null;
        const includeUnmapped = this.props.showMappedKpis !== false;
        let fullRowsWithGroups;

        if (includeUnmapped && indexes) {
            fullRowsWithGroups = this.buildHierarchyRows();
        } else {
        const resolveCtx = (m) => {
            if (indexes) return indexes.resolveMappingContext(m);
            const k = kpis.find(item => String(item.id) === String(m.kpiId));
            const mdl = k ? null : models.find(item => String(item.id) === String(m.modelId));
            const item = k || mdl;
            if (!item) return null;
            const f = functions.find(fnItem => String(fnItem.id) === String(item.functionId));
            const b = f ? businesses.find(bizItem => String(bizItem.id) === String(f.businessId)) : null;
            return { kpi: k, model: mdl, item, fn: f, biz: b, isModel: !!mdl, effectivelyActive: true };
        };

        const filteredRows = mappings.filter(m => {
            const ctx = resolveCtx(m);
            if (!ctx || !ctx.item) return false;
            const { biz: b, fn: f, kpi: k, model: mdl } = ctx;

            if (selectedNode && selectedNode.type === 'business' && b && b.id !== selectedNode.id) return false;
            if (selectedNode && selectedNode.type === 'function' && f && f.id !== selectedNode.id) return false;
            if (selectedNode && selectedNode.type === 'kpi' && k && k.id !== selectedNode.id) return false;
            if (selectedNode && selectedNode.type === 'model' && mdl && mdl.id !== selectedNode.id) return false;

            return true;
        });

        // Group filtered mappings by Business -> Function -> KPI/model
        const { collapsedGroups } = this.state;
        const byBiz = new Map();

        filteredRows.forEach(m => {
            const ctx = resolveCtx(m);
            if (!ctx || !ctx.item) return;
            const b = ctx.biz || { id: 'unknown', name: 'Other Business' };
            const f = ctx.fn || { id: 'unknown', name: 'General Function' };
            const bId = String(b.id);
            const fId = String(f.id);
            
            if (!byBiz.has(bId)) {
                byBiz.set(bId, { biz: b, functions: new Map(), mappings: [] });
            }
            const bizGroup = byBiz.get(bId);
            bizGroup.mappings.push(m);
            
            if (!bizGroup.functions.has(fId)) {
                bizGroup.functions.set(fId, { fn: f, mappings: [] });
            }
            bizGroup.functions.get(fId).mappings.push(m);
        });

        fullRowsWithGroups = [];

        // Prefer groups derived from mappings so orphan business/function links still render
        const bizGroupsToRender = [];
        const seenBizIds = new Set();
        businesses.forEach((b) => {
            const bId = String(b.id);
            const bizGroup = byBiz.get(bId);
            if (!bizGroup) return;
            seenBizIds.add(bId);
            bizGroupsToRender.push({ biz: b, bizGroup });
        });
        byBiz.forEach((bizGroup, bId) => {
            if (seenBizIds.has(bId)) return;
            bizGroupsToRender.push({ biz: bizGroup.biz, bizGroup });
        });

        bizGroupsToRender.forEach(({ biz: b, bizGroup }) => {
            const bId = String(b.id);
            const bKey = `biz:${bId}`;
            const isBizOpen = !collapsedGroups.has(bKey);
            const fnCount = bizGroup.functions.size;
            const totalBizKpis = new Set(bizGroup.mappings.map(m => m.kpiId)).size;

            // Business Header Row
            fullRowsWithGroups.push({
                id: bKey,
                isBizRow: true,
                bKey,
                biz: b,
                isExpanded: isBizOpen,
                fnCount,
                kpiCount: totalBizKpis
            });

            if (isBizOpen) {
                const funcList = [];
                const seenFnIds = new Set();
                functions
                    .filter(f => String(f.businessId) === bId && bizGroup.functions.has(String(f.id)))
                    .forEach((f) => {
                        seenFnIds.add(String(f.id));
                        funcList.push(f);
                    });
                bizGroup.functions.forEach((fnGroup, fId) => {
                    if (seenFnIds.has(String(fId))) return;
                    funcList.push(fnGroup.fn);
                });

                funcList.forEach((f, fIdx) => {
                    const fId = String(f.id);
                    const fnGroup = bizGroup.functions.get(fId);
                    if (!fnGroup) return;
                    const fKey = `fn:${fId}`;
                    const isFnOpen = !collapsedGroups.has(fKey);
                    const isFnLast = fIdx === funcList.length - 1;
                    const fnKpis = new Set(fnGroup.mappings.map(m => m.kpiId)).size;

                    // Function Header Row
                    fullRowsWithGroups.push({
                        id: fKey,
                        isFnRow: true,
                        fKey,
                        fn: f,
                        biz: b,
                        isExpanded: isFnOpen,
                        isFnLast,
                        kpiCount: fnKpis
                    });

                    if (isFnOpen) {
                        const groupedByKpi = new Map();
                        fnGroup.mappings.forEach(m => {
                            const ctx = resolveCtx(m);
                            if (!ctx || !ctx.item) return;
                            const asset = indexes
                                ? indexes.getAsset(m.assetId)
                                : this.props.assets.find(a => String(a.id) === String(m.assetId));
                            const itemKey = String(ctx.item.id ?? ctx.item.name ?? m.kpiId ?? m.id);
                            const datasourceKey = asset?.datasource
                                ? String(asset.datasource)
                                : `asset:${m.assetId ?? m.id}`;
                            const key = `${itemKey}::${datasourceKey}`;
                            if (!groupedByKpi.has(key)) {
                                groupedByKpi.set(key, { mappings: [], ctx });
                            }
                            groupedByKpi.get(key).mappings.push(m);
                        });

                        const leafList = Array.from(groupedByKpi.values());
                        leafList.forEach((group, leafIdx) => {
                            const isLeafLast = leafIdx === leafList.length - 1;
                            const { mappings: groupMappings, ctx } = group;
                            const { item, kpi: k, model: mdl } = ctx;

                            const mappedAssets = groupMappings
                                .map(gm => (indexes ? indexes.getAsset(gm.assetId) : this.props.assets.find(a => a.id === gm.assetId)))
                                .filter(Boolean);
                            const uniqueAssetsById = new Map();
                            mappedAssets.forEach(a => { if (a && a.id != null) uniqueAssetsById.set(a.id, a); });
                            const uniqueMappedAssets = Array.from(uniqueAssetsById.values());
                            if (this.props.showMappedKpis === false && uniqueMappedAssets.length === 0) return;
                            const datasources = [...new Set(uniqueMappedAssets.map(a => a.datasource).filter(Boolean))];
                            const status = groupMappings.some(gm => normalizeMappingStatus(gm.status) === MAPPING_STATUS.MANUAL)
                                ? MAPPING_STATUS.MANUAL
                                : normalizeMappingStatus(groupMappings[0]?.status);

                            fullRowsWithGroups.push({
                                id: `leaf_${b.id}_${f.id}_${item.id}_${leafIdx}`,
                                entityKey: `${mdl ? 'model' : 'kpi'}:${item.id}`,
                                isLeafRow: true,
                                isFnLast,
                                isLeafLast,
                                biz: b,
                                fn: f,
                                bizName: b ? b.name : '',
                                fnName: f ? f.name : '',
                                kpiName: item.name,
                                formula: k ? k.formula : '',
                                datasources,
                                mappedAssets: uniqueMappedAssets,
                                status,
                                mappingIds: groupMappings.map(gm => gm.id),
                                kpiId: k ? k.id : (mdl ? mdl.id : groupMappings[0]?.kpiId),
                                kpi_store_id: mdl ? null : (k?.kpi_store_id ?? k?.kpiStoreId ?? null),
                                functionId: f ? f.id : (k ? k.functionId : null),
                                businessId: b ? b.id : null,
                                assetId: groupMappings[0]?.assetId,
                                isModel: !!mdl,
                                modelKind: mdl ? mdl.kind : ''
                            });
                        });
                    }
                });
            }
        });
        }

        // A KPI may have one row per datasource. Merge only its hierarchy cell
        // while keeping datasource-specific formula/asset/status/action cells separate.
        for (let rowIndex = 0; rowIndex < fullRowsWithGroups.length; rowIndex += 1) {
            const row = fullRowsWithGroups[rowIndex];
            if (!row.isLeafRow || !row.entityKey) continue;

            let nextIndex = rowIndex + 1;
            while (
                nextIndex < fullRowsWithGroups.length
                && fullRowsWithGroups[nextIndex].isLeafRow
                && fullRowsWithGroups[nextIndex].entityKey === row.entityKey
            ) {
                nextIndex += 1;
            }

            const rowSpan = nextIndex - rowIndex;
            if (rowSpan > 1) {
                row.hierarchyRowSpan = rowSpan;
                for (let continuationIndex = rowIndex + 1; continuationIndex < nextIndex; continuationIndex += 1) {
                    fullRowsWithGroups[continuationIndex].hideHierarchyCell = true;
                }
                rowIndex = nextIndex - 1;
            }
        }

        const formattedRows = fullRowsWithGroups.map((rowItem) => {
            return [
                { type: "td", value: rowItem.kpiName || rowItem.biz?.name || rowItem.fn?.name || "", row: rowItem },
                { type: "td", value: rowItem.formula || rowItem.modelKind || "", row: rowItem },
                { type: "td", value: (rowItem.datasources || []).join(',') || "", row: rowItem },
                { type: "td", value: rowItem.mappedAssets ? String(rowItem.mappedAssets.length) : "", row: rowItem },
                { type: "td", value: rowItem.status || "", row: rowItem },
                { type: "component", value: "", row: rowItem }
            ];
        });

        const targetLimit = 15;
        const realRowsCount = formattedRows.length;

        const headers = ["Business Hierarchy", "Formula", "Datasources", "Mapped Data fields", "Status", "Actions"];

        const tableData = {
            headers: headers,
            data: formattedRows,
            not_sortable: true
        };

        const pageOptions = [15, 25, 50, 100];
        const selectedCount = this.state.selectedMappings.size;

        return (
            <>


                <style>{`
                    #domain-mapping-table-wrapper table tbody tr {
                        height: 100% !important;
                    }
                    #domain-mapping-table-wrapper table tbody tr > td:first-child {
                        height: 100% !important;
                    }
                    #domain-mapping-table-wrapper table tbody tr > td:first-child > div {
                        height: 100% !important;
                    }
                    ${realRowsCount <= targetLimit ? `.react-bootstrap-table__footer { display: none !important; }` : ''}
                `}</style>

                {selectedCount > 0 && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-[10px] tw-mb-2 tw-bg-blue-50 dark:tw-bg-[#13131F] tw-border-0 tw-border-b tw-border-solid tw-border-[#ECECEC] dark:tw-border-[#334155]">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-font-semibold tw-text-white tw-bg-[#2563eb] dark:tw-bg-[#3B82F6] tw-rounded-full tw-pl-1.5 tw-pr-2.5 tw-py-1">
                                <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-full tw-bg-white/30 dark:tw-bg-white/20 tw-text-[11px] tw-font-bold">{selectedCount}</span>
                                <span className="tw-font-semibold">selected</span>
                            </span>
                            <span className="tw-text-xs tw-font-medium tw-text-[#6B7280] dark:tw-text-[#94A3B8]">
                                mapping{selectedCount === 1 ? '' : 's'}
                            </span>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-2">
                            <button
                                type="button"
                                onClick={this.handleBulkEnableClick}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#334059] dark:tw-text-[#E2E8F0] tw-bg-white dark:tw-bg-[#1E293B] tw-border tw-border-solid tw-border-[#D1D5DB] dark:tw-border-[#334155] tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#273449] tw-transition-colors"
                            >
                                <FontAwesomeIcon icon={faToggleOn} className="tw-text-[#A4A7AE] dark:tw-text-[#34D399]" style={{ fontSize: '18px' }} />
                                Enable
                            </button>
                            <button
                                type="button"
                                onClick={this.handleBulkDisableClick}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#334059] dark:tw-text-[#E2E8F0] tw-bg-white dark:tw-bg-[#1E293B] tw-border tw-border-solid tw-border-[#D1D5DB] dark:tw-border-[#334155] tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#273449] tw-transition-colors"
                            >
                                <FontAwesomeIcon icon={faToggleOff} className="tw-text-[#A4A7AE] dark:tw-text-[#94A3B8]" style={{ fontSize: '18px' }} />
                                Disable
                            </button>
                            <button
                                type="button"
                                onClick={this.clearSelection}
                                className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-text-[#6B7280] dark:tw-text-[#94A3B8] tw-bg-transparent tw-border-none tw-rounded-full tw-cursor-pointer hover:tw-bg-blue-100 dark:hover:tw-bg-white/10 tw-transition-colors tw-p-0"
                                aria-label="Clear selection"
                            >
                                <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}



                <div id="domain-mapping-table-wrapper" className='domain-mapping-table hide-tbl-border-radius tw-mt-[0px] !tw-overflow-y-visible'>
                    <QualdoDataTable
                        id="domainMappingTable"
                        component_name="domainMappingTable"
                        classNameForTable="table"
                        wrapperClasses="table-responsive action !tw-border-0"
                        data={tableData}
                        hidePagination={true}
                        pageOptions={pageOptions}
                        paginationSize={999999}
                        customGetActionComponent={this.customGetActionComponent}
                        customRowHighlighter={this.customRowHighlighter}
                    />
                </div>

                <WorkflowModal
                    show={this.state.showWorkflowModal}
                    row={this.state.workflowRow}
                    data={this.state.diagnosticMapData}
                    loading={this.state.diagnosticMapLoading}
                    error={this.state.diagnosticMapError}
                    onClose={this.closeWorkflow}
                />
            </>
        );
    }
}

export default DomainMappingTable;
